import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  BackofficePlan,
  BackofficePlanFeatureDefinition,
  BackofficePlanUpsertPayload
} from './backoffice.models';
import { BackofficeService } from './backoffice.service';

@Component({
  selector: 'app-backoffice-plans-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './plans-page.component.html',
  styleUrl: './plans-page.component.css'
})
export class BackofficePlansPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly backofficeService = inject(BackofficeService);

  readonly plans = computed(() =>
    [...this.backofficeService.planOptions()].sort((left, right) =>
      Number(left.sortOrder || 0) - Number(right.sortOrder || 0)
      || left.name.localeCompare(right.name)
    )
  );
  readonly featureCatalog = this.backofficeService.planFeatureCatalog;
  readonly selectedPlanKey = signal<string | null>(null);
  readonly saving = signal(false);
  readonly actionPlanKey = signal<string | null>(null);
  readonly feedback = signal<string | null>(null);
  readonly feedbackType = signal<'success' | 'error'>('success');

  readonly activePlanCount = computed(() => this.plans().filter((plan) => plan.active).length);
  readonly selfServePlanCount = computed(() =>
    this.plans().filter((plan) => plan.active && this.hasStripePrice(plan)).length
  );

  readonly form = this.fb.group({
    key: this.fb.nonNullable.control('', Validators.required),
    name: this.fb.nonNullable.control('', Validators.required),
    monthlyPriceEuros: this.fb.nonNullable.control(0),
    displayPriceLabel: this.fb.nonNullable.control(''),
    stripePriceId: this.fb.nonNullable.control(''),
    description: this.fb.nonNullable.control('', Validators.required),
    badge: this.fb.nonNullable.control(''),
    sortOrder: this.fb.nonNullable.control(0),
    active: this.fb.nonNullable.control(true),
    highlight: this.fb.nonNullable.control(false),
    features: this.fb.group({}),
  });

  constructor() {
    effect(() => {
      this.syncFeatureControls(this.featureCatalog());
    });
  }

  ngOnInit(): void {
    this.backofficeService.loadOverview();
    this.startCreate();
  }

  startCreate(): void {
    this.selectedPlanKey.set(null);
    this.feedback.set(null);
    this.form.patchValue({
      key: '',
      name: '',
      monthlyPriceEuros: 0,
      displayPriceLabel: '',
      stripePriceId: '',
      description: '',
      badge: '',
      sortOrder: this.nextSortOrder(),
      active: true,
      highlight: false,
    }, { emitEvent: false });
    this.resetFeatureControls([]);
  }

  editPlan(plan: BackofficePlan): void {
    this.selectedPlanKey.set(plan.key);
    this.feedback.set(null);
    this.form.patchValue({
      key: plan.key,
      name: plan.name,
      monthlyPriceEuros: Number(plan.monthlyPriceEuros || 0),
      displayPriceLabel: plan.displayPriceLabel || '',
      stripePriceId: plan.stripePriceId || '',
      description: plan.description || '',
      badge: plan.badge || '',
      sortOrder: Number(plan.sortOrder || 0),
      active: Boolean(plan.active),
      highlight: Boolean(plan.highlight),
    }, { emitEvent: false });
    this.resetFeatureControls(plan.features || []);
  }

  submit(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    const payload: BackofficePlanUpsertPayload = {
      key: rawValue.key.trim(),
      name: rawValue.name.trim(),
      monthlyPriceEuros: Number(rawValue.monthlyPriceEuros || 0),
      displayPriceLabel: this.resolveDisplayPriceLabel(
        rawValue.displayPriceLabel,
        rawValue.monthlyPriceEuros
      ),
      stripePriceId: rawValue.stripePriceId.trim(),
      description: rawValue.description.trim(),
      badge: rawValue.badge.trim(),
      sortOrder: Number(rawValue.sortOrder || 0),
      active: Boolean(rawValue.active),
      highlight: Boolean(rawValue.highlight),
      features: this.selectedFeatureKeys(),
    };

    this.saving.set(true);
    this.feedback.set(null);

    this.backofficeService.savePlan(payload).subscribe({
      next: () => {
        this.saving.set(false);
        this.feedbackType.set('success');
        this.feedback.set(this.selectedPlanKey() ? 'Plan mis a jour.' : 'Plan cree.');
        this.startCreate();
      },
      error: (error) => {
        this.saving.set(false);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  togglePlanActive(plan: BackofficePlan): void {
    this.actionPlanKey.set(plan.key);
    this.feedback.set(null);

    this.backofficeService.savePlan({
      key: plan.key,
      name: plan.name,
      monthlyPriceEuros: Number(plan.monthlyPriceEuros || 0),
      displayPriceLabel: plan.displayPriceLabel || '',
      stripePriceId: plan.stripePriceId || '',
      description: plan.description || '',
      badge: plan.badge || '',
      sortOrder: Number(plan.sortOrder || 0),
      active: !plan.active,
      highlight: Boolean(plan.highlight),
      features: plan.features || [],
    }).subscribe({
      next: () => {
        this.actionPlanKey.set(null);
        this.feedbackType.set('success');
        this.feedback.set(plan.active ? 'Plan desactive.' : 'Plan reactive.');
        if (this.selectedPlanKey() === plan.key) {
          this.editPlan({ ...plan, active: !plan.active });
        }
      },
      error: (error) => {
        this.actionPlanKey.set(null);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  deletePlan(plan: BackofficePlan): void {
    const confirmed = window.confirm(`Supprimer le plan ${plan.name} ?`);
    if (!confirmed) {
      return;
    }

    this.actionPlanKey.set(plan.key);
    this.feedback.set(null);

    this.backofficeService.deletePlan(plan.key).subscribe({
      next: () => {
        this.actionPlanKey.set(null);
        this.feedbackType.set('success');
        this.feedback.set('Plan supprime.');
        if (this.selectedPlanKey() === plan.key) {
          this.startCreate();
        }
      },
      error: (error) => {
        this.actionPlanKey.set(null);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  trackPlan(_: number, plan: BackofficePlan): string {
    return plan.key;
  }

  trackFeature(_: number, feature: BackofficePlanFeatureDefinition): string {
    return feature.key;
  }

  get featureGroup(): FormGroup {
    return this.form.get('features') as FormGroup;
  }

  featureControl(featureKey: string): FormControl<boolean> {
    const existingControl = this.featureGroup.get(featureKey);
    if (existingControl instanceof FormControl) {
      return existingControl as FormControl<boolean>;
    }

    const control = new FormControl(false, { nonNullable: true });
    this.featureGroup.addControl(featureKey, control);
    return control;
  }

  hasStripePrice(plan: BackofficePlan): boolean {
    return Boolean(String(plan.stripePriceId || '').trim());
  }

  featureLabel(featureKey: string): string {
    return this.featureCatalog().find((feature) => feature.key === featureKey)?.label || featureKey;
  }

  private syncFeatureControls(features: BackofficePlanFeatureDefinition[]): void {
    const featureKeys = features.map((feature) => feature.key);
    const existingKeys = Object.keys(this.featureGroup.controls);

    for (const featureKey of featureKeys) {
      if (!this.featureGroup.contains(featureKey)) {
        this.featureGroup.addControl(featureKey, new FormControl(false, { nonNullable: true }));
      }
    }

    for (const featureKey of existingKeys) {
      if (!featureKeys.includes(featureKey)) {
        this.featureGroup.removeControl(featureKey);
      }
    }
  }

  private resetFeatureControls(selectedFeatureKeys: string[]): void {
    const selectedSet = new Set(selectedFeatureKeys);
    this.featureGroup.patchValue(
      Object.fromEntries(this.featureCatalog().map((feature) => [feature.key, selectedSet.has(feature.key)])),
      { emitEvent: false }
    );
  }

  private selectedFeatureKeys(): string[] {
    return Object.entries(this.featureGroup.getRawValue())
      .filter(([, enabled]) => Boolean(enabled))
      .map(([featureKey]) => featureKey);
  }

  private nextSortOrder(): number {
    const currentOrders = this.plans().map((plan) => Number(plan.sortOrder || 0));
    return currentOrders.length === 0 ? 10 : Math.max(...currentOrders) + 10;
  }

  private resolveDisplayPriceLabel(rawLabel: string, rawMonthlyPriceEuros: number): string {
    const label = String(rawLabel || '').trim();
    if (label) {
      return label;
    }

    const numericPrice = Number(rawMonthlyPriceEuros || 0);
    return numericPrice > 0 ? `${numericPrice}€ / user / month` : 'Custom pricing';
  }

  private formatError(code: string | undefined): string {
    switch (code) {
      case 'PLAN_KEY_REQUIRED':
        return 'La cle du plan est obligatoire.';
      case 'PLAN_NAME_REQUIRED':
        return 'Le nom du plan est obligatoire.';
      case 'PLAN_FEATURES_REQUIRED':
        return 'Selectionnez au moins une feature.';
      case 'PLAN_NAME_ALREADY_EXISTS':
        return 'Un plan porte deja ce nom.';
      case 'PLAN_PRICE_INVALID':
        return 'Le prix du plan est invalide.';
      case 'PLAN_SORT_ORDER_INVALID':
        return 'L ordre du plan est invalide.';
      case 'PLAN_IN_USE_BY_COMPANY':
        return 'Ce plan ne peut pas etre supprime car une compagnie l utilise encore.';
      case 'PLAN_IN_USE_BY_USER':
        return 'Ce plan ne peut pas etre supprime car un utilisateur l utilise encore.';
      case 'SUPERADMIN_REQUIRED':
        return 'Cette action est reservee au superadmin.';
      default:
        return 'L action sur le plan a echoue.';
    }
  }
}
